import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Search, Image, ChevronRight, Clock, Tag, Eye, ToggleLeft, ToggleRight, Trash2, X, Heart } from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getSearchBasedLibrary, getSearchClassificationImages, deleteEpisodeFromHistory, getAllFavorites, addWorkFavorite, removeWorkFavorite, addEpisodeFavorite, removeEpisodeFavorite, request, type SearchBasedCategoryInfo, type SearchBasedEpisodeInfo } from '../api/fancaps'

// 时间格式化函数
const formatDateTime = (dateString: string): string => {
  if (!dateString) return '未知'
  
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return '未知'
    
    // 格式化为 "2025-01-15 14:30"
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).replace(/\//g, '-')
  } catch (error) {
    console.error('时间格式化失败:', error)
    return '未知'
  }
}

const SearchBasedLibraryPage: React.FC = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [selectedCategory, setSelectedCategory] = useState<SearchBasedCategoryInfo | null>(null)
  const [displayMode, setDisplayMode] = useState<'by_work' | 'by_episode'>('by_work') // 显示模式
  
  // 删除功能相关状态
  const [deleteConfirm, setDeleteConfirm] = useState<{
    show: boolean
    episode: SearchBasedEpisodeInfo | null
    workName: string
  }>({
    show: false,
    episode: null,
    workName: ''
  })
  const [isDeleting, setIsDeleting] = useState(false)
  // 移除selectedEpisode状态，因为现在直接跳转到图片页面
  // const [selectedEpisode, setSelectedEpisode] = useState<SearchBasedEpisodeInfo | null>(null)
  // const [currentPage, setCurrentPage] = useState(1)
  // const limit = 10000 // 和后端保持一致，支持电影级大量图片

  // 获取搜索历史分类数据
  const {
    data: libraryData,
    isLoading,
    error
  } = useQuery({
    queryKey: ['search-based-library'],
    queryFn: getSearchBasedLibrary,
    staleTime: 5 * 60 * 1000, // 5分钟缓存
  })

  // 移除单集图片查询，因为现在直接跳转
  // const {
  //   data: episodeImagesData,
  //   isLoading: isLoadingEpisode
  // } = useQuery({
  //   queryKey: ['search-classification-images', selectedEpisode?.classification_name, currentPage],
  //   queryFn: () => {
  //     if (!selectedEpisode) return null
  //     return getSearchClassificationImages(selectedEpisode.classification_name, currentPage, limit)
  //   },
  //   enabled: !!selectedEpisode,
  //   staleTime: 10 * 60 * 1000,
  // })

  // 获取收藏数据
  const { data: favoritesData } = useQuery({
    queryKey: ['favorites'],
    queryFn: getAllFavorites,
    staleTime: 30 * 1000,
  })

  // 处理作品收藏
  const handleWorkFavorite = async (category: SearchBasedCategoryInfo, isFavorited: boolean) => {
    try {
      if (isFavorited) {
        const workFavorite = favoritesData?.data?.works?.find(w => w.title === category.show_name)
        if (workFavorite) {
          await removeWorkFavorite(workFavorite.id)
        }
      } else {
        // 🎯 修复：通过搜索API获取正确的作品ID，而不是使用集数ID
        console.log('🔍 开始获取正确的作品ID for:', category.show_name)
        
        try {
          const searchData = await request<{results: any[]}>('/search/', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              query: category.show_name.toLowerCase(),
              content_type: 'all'
            })
          })
          let workUrl = `search-history:${category.show_name}` // 默认回退URL
          
          if (searchData.results && searchData.results.length > 0) {
            // 🎯 精确匹配作品标题
            const normalizeTitle = (title: string) => {
              return title.toLowerCase()
                .replace(/[^\w\s]/g, '') // 移除标点符号
                .replace(/\s+/g, ' ') // 标准化空格
                .trim()
            }
            
            const normalizedWorkTitle = normalizeTitle(category.show_name)
            console.log('🔍 标准化标题:', normalizedWorkTitle)
            
            // 查找最匹配的搜索结果
            const exactMatch = searchData.results.find((result: any) => {
              const normalizedResultTitle = normalizeTitle(result.title)
              console.log('🔍 比较:', normalizedResultTitle, '===', normalizedWorkTitle)
              return normalizedResultTitle === normalizedWorkTitle
            })
            
            if (exactMatch) {
              // 从搜索结果的URL中提取正确的show_id
              const urlMatch = exactMatch.url.match(/showimages\.php\?(.+)$/)
              if (urlMatch) {
                const correctShowId = urlMatch[1]
                workUrl = `https://fancaps.net/anime/showimages.php?${correctShowId}`
                console.log('✅ 找到正确的作品URL:', workUrl)
              }
            } else {
              // 尝试模糊匹配
              const partialMatch = searchData.results.find((result: any) => {
                const normalizedResultTitle = normalizeTitle(result.title)
                return normalizedResultTitle.includes(normalizedWorkTitle) || 
                       normalizedWorkTitle.includes(normalizedResultTitle)
              })
              
              if (partialMatch) {
                const urlMatch = partialMatch.url.match(/showimages\.php\?(.+)$/)
                if (urlMatch) {
                  const correctShowId = urlMatch[1]
                  workUrl = `https://fancaps.net/anime/showimages.php?${correctShowId}`
                  console.log('✅ 找到模糊匹配的作品URL:', workUrl, '作品:', partialMatch.title)
                }
              }
            }
          }
          
          await addWorkFavorite(category.show_name, 'Anime', workUrl)
          console.log('✅ 成功收藏作品:', category.show_name, 'URL:', workUrl)
        } catch (searchError) {
          console.error('❌ 搜索API失败，使用回退URL:', searchError)
          // 使用回退URL
          await addWorkFavorite(category.show_name, 'Anime', `search-history:${category.show_name}`)
        }
      }
      queryClient.invalidateQueries({ queryKey: ['favorites'] })
    } catch (error) {
      console.error('收藏操作失败:', error)
    }
  }

  // 处理剧集收藏
  const handleEpisodeFavorite = async (episode: SearchBasedEpisodeInfo, workName: string, isFavorited: boolean) => {
    try {
      if (isFavorited) {
        const episodeFavorite = favoritesData?.data?.episodes?.find(e => 
          e.title === episode.episode_name && e.work_title === workName
        )
        if (episodeFavorite) {
          await removeEpisodeFavorite(episodeFavorite.id)
        }
      } else {
        // 使用与handleEpisodeClick相同的URL格式
        const episodeUrl = episode.episode_id // 直接使用episode_id，不添加路径前缀
        const previewImages: string[] = []
        
        await addEpisodeFavorite(
          episode.episode_name,
          workName,
          workName,
          episodeUrl,
          previewImages
        )
      }
      queryClient.invalidateQueries({ queryKey: ['favorites'] })
    } catch (error) {
      console.error('收藏操作失败:', error)
    }
  }

  // 检查作品是否已收藏
  const isWorkFavorited = (workName: string): boolean => {
    return favoritesData?.data?.works?.some(work => work.title === workName) || false
  }

  // 检查剧集是否已收藏
  const isEpisodeFavorited = (episodeName: string, workName: string): boolean => {
    return favoritesData?.data?.episodes?.some(episode => 
      episode.title === episodeName && episode.work_title === workName
    ) || false
  }

  const handleBackClick = () => {
    // 简化返回逻辑
    if (selectedCategory) {
      setSelectedCategory(null)
    } else {
      navigate('/')
    }
  }

  const handleCategoryClick = (category: SearchBasedCategoryInfo) => {
    setSelectedCategory(category)
    // setSelectedEpisode(null) // 移除selectedEpisode
    // setCurrentPage(1) // 移除currentPage
  }

  const handleEpisodeClick = (episode: SearchBasedEpisodeInfo) => {
    // 🎯 修复：智能处理episode_id，可能是完整URL或只是ID
    let episodeUrl = episode.episode_id
    
    // 如果不是完整URL，需要构造完整URL
    if (!episodeUrl.startsWith('https://fancaps.net/')) {
      // 对于搜索历史库，由于我们无法确定具体是TV还是动漫，暂时默认使用动漫路径
      // 后端会通过双重尝试策略自动找到正确的路径
      if (!episodeUrl.startsWith('/images/')) {
        episodeUrl = `https://fancaps.net/anime/episodeimages.php?${episodeUrl}`
      } else {
        // 如果是 /images/ 格式，提取ID后构造
        const episodeId = episodeUrl.replace('/images/', '')
        episodeUrl = `https://fancaps.net/anime/episodeimages.php?${episodeId}`
      }
    }
    
    console.log('🔗 SearchBasedLibrary 传递完整URL到后端:', episodeUrl)
    navigate(`/images/${encodeURIComponent(episodeUrl)}`)
  }

  const handleDisplayModeToggle = () => {
    setDisplayMode(prev => prev === 'by_work' ? 'by_episode' : 'by_work')
  }

  // 删除功能处理函数
  const handleDeleteClick = (episode: SearchBasedEpisodeInfo, workName: string, event: React.MouseEvent) => {
    event.stopPropagation() // 阻止事件冒泡，避免触发剧集点击
    setDeleteConfirm({
      show: true,
      episode,
      workName
    })
  }

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.episode) return
    
    setIsDeleting(true)
    try {
      const result = await deleteEpisodeFromHistory(deleteConfirm.episode.episode_id)
      
      if (result.success) {
        // 刷新数据
        queryClient.invalidateQueries(['search-based-library'])
        
        // 如果作品被删除了，且当前正在查看这个作品，返回到作品列表
        if (result.work_removed && selectedCategory?.show_name === result.work_title) {
          setSelectedCategory(null)
        }
        
        // 显示成功消息
        alert(result.message)
      } else {
        alert(`删除失败: ${result.message}`)
      }
    } catch (error) {
      console.error('删除失败:', error)
      
      // 更详细的错误信息
      let errorMessage = '删除失败，请重试'
      if (error instanceof Error) {
        errorMessage = `删除失败: ${error.message}`
      } else if (error && typeof error === 'object' && 'status' in error) {
        errorMessage = `删除失败: HTTP ${error.status}`
      }
      
      alert(errorMessage)
    } finally {
      setIsDeleting(false)
      setDeleteConfirm({ show: false, episode: null, workName: '' })
    }
  }

  const handleDeleteCancel = () => {
    setDeleteConfirm({ show: false, episode: null, workName: '' })
  }

  // 生成按集数显示的数据
  const getEpisodesList = () => {
    if (!libraryData?.categories) return []
    
    const allEpisodes: (SearchBasedEpisodeInfo & { work_name: string })[] = []
    
    libraryData.categories.forEach(category => {
      category.episodes.forEach(episode => {
        allEpisodes.push({
          ...episode,
          work_name: category.show_name
        })
      })
    })
    
    // 按最近浏览时间降序排序
    allEpisodes.sort((a, b) => {
      const timeA = new Date(a.created_at).getTime()
      const timeB = new Date(b.created_at).getTime()
      return timeB - timeA
    })
    
    return allEpisodes
  }

  const getImageProxyUrl = (imageId: string) => {
    return `/api/images/${imageId}.jpg`
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-blue-600 hover:text-blue-700 mr-4"
          >
            <ArrowLeft className="w-5 h-5 mr-1" />
            返回首页
          </button>
          <h1 className="text-2xl font-bold text-gray-900">搜索历史分类库</h1>
        </div>
        
        <div className="max-w-2xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <p className="text-red-700">
              加载搜索历史分类库失败: {error instanceof Error ? error.message : '未知错误'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 删除确认对话框 */}
      {deleteConfirm.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">确认删除</h3>
              <button
                onClick={handleDeleteCancel}
                className="text-gray-400 hover:text-gray-600"
                disabled={isDeleting}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 mb-2">
                确定要删除以下集数吗？
              </p>
              <div className="bg-gray-50 p-3 rounded">
                <p className="font-medium text-gray-900">
                  {deleteConfirm.workName} - {deleteConfirm.episode?.episode_name}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {deleteConfirm.episode?.image_count} 张图片
                </p>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                注意：这只会从搜索历史中移除记录，不会影响实际的图片缓存。
              </p>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleDeleteCancel}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
                disabled={isDeleting}
              >
                取消
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors flex items-center"
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    删除中...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    确认删除
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 标题和导航 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <button
            onClick={handleBackClick}
            className="flex items-center text-blue-600 hover:text-blue-700 mr-4"
          >
            <ArrowLeft className="w-5 h-5 mr-1" />
            {selectedCategory ? '返回作品列表' : '返回首页'}
          </button>
          
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <span>搜索历史分类库</span>
            {selectedCategory && (
              <>
                <ChevronRight className="w-4 h-4" />
                <span>{selectedCategory.show_name}</span>
              </>
            )}
            {/* {selectedEpisode && (
              <>
                <ChevronRight className="w-4 h-4" />
                <span>{selectedEpisode.episode_name}</span>
              </>
            )} */}
          </div>
        </div>

        {libraryData && (
          <div className="text-sm text-gray-600">
            共 {libraryData.total_categories} 个作品，{libraryData.total_images} 张图片
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">正在加载搜索历史分类库...</p>
          </div>
        </div>
      ) : selectedCategory ? (
        // 显示选中作品的集数列表
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h2 className="text-lg font-medium text-blue-900 mb-2">
              {selectedCategory.show_name}
            </h2>
            <p className="text-blue-700 text-sm">
              共 {selectedCategory.episode_count} 集，{selectedCategory.total_images} 张图片
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {selectedCategory.episodes.map((episode, index) => {
              const isFavorited = isEpisodeFavorited(episode.episode_name, selectedCategory.show_name)
              return (
                <div
                  key={`${episode.episode_id}-${index}`}
                  className="group bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-lg transition-all relative"
                >
                  {/* 收藏按钮 */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleEpisodeFavorite(episode, selectedCategory.show_name, isFavorited)
                    }}
                    className={`absolute top-3 right-3 z-10 p-1.5 rounded-full transition-all ${
                      isFavorited 
                        ? 'text-red-500 hover:text-red-600 bg-red-50 hover:bg-red-100' 
                        : 'text-gray-400 hover:text-red-500 bg-white hover:bg-red-50'
                    } shadow-sm border border-gray-200`}
                    title={isFavorited ? '取消收藏' : '收藏剧集'}
                  >
                    <Heart className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`} />
                  </button>

                  <div 
                    onClick={() => handleEpisodeClick(episode)}
                    className="cursor-pointer"
                  >
                    <div className="flex items-start justify-between mb-3 pr-10">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 group-hover:text-blue-600 truncate">
                          {episode.episode_name}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                          来源搜索：{episode.search_term}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2 flex-shrink-0 ml-3">
                        <button
                          onClick={(e) => handleDeleteClick(episode, selectedCategory.show_name, e)}
                          className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="删除此集数"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600" />
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center space-x-4 text-gray-600">
                        <span className="flex items-center">
                          <Image className="w-4 h-4 mr-1" />
                          {episode.image_count} 张图片
                        </span>
                        <span className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          {formatDateTime(episode.created_at)}
                        </span>
                      </div>
                      <span className="text-blue-600 font-medium text-sm bg-blue-50 px-2 py-1 rounded">
                        查看图片
                      </span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ) : (
        // 显示作品列表
        <div className="space-y-4">
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-medium text-orange-900">
                🔍 搜索历史智能分类库
              </h2>
              
              {/* 显示模式切换器 */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">按作品</span>
                <button
                  onClick={handleDisplayModeToggle}
                  className="relative inline-flex items-center"
                >
                  {displayMode === 'by_work' ? (
                    <ToggleLeft className="w-8 h-8 text-gray-400 hover:text-blue-600 transition-colors" />
                  ) : (
                    <ToggleRight className="w-8 h-8 text-blue-600 hover:text-blue-700 transition-colors" />
                  )}
                </button>
                <span className="text-sm text-gray-600">按集数</span>
              </div>
            </div>
            <p className="text-orange-700 text-sm">
              基于您的搜索历史自动创建分类，如"Clannad - 第8集"，让图片管理更智能
            </p>
          </div>

          {libraryData && libraryData.categories.length > 0 ? (
            <>
              {/* 按作品显示模式 */}
              {displayMode === 'by_work' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {libraryData.categories.map((category, index) => {
                    const isFavorited = isWorkFavorited(category.show_name)
                    return (
                      <div
                        key={`${category.show_name}-${index}`}
                        className="group bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-lg transition-all relative"
                      >
                        {/* 收藏按钮 */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleWorkFavorite(category, isFavorited)
                          }}
                          className={`absolute top-3 right-3 z-10 p-1.5 rounded-full transition-all ${
                            isFavorited 
                              ? 'text-red-500 hover:text-red-600 bg-red-50 hover:bg-red-100' 
                              : 'text-gray-400 hover:text-red-500 bg-white hover:bg-red-50'
                          } shadow-sm border border-gray-200`}
                          title={isFavorited ? '取消收藏' : '收藏作品'}
                        >
                          <Heart className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`} />
                        </button>

                        <div 
                          onClick={() => handleCategoryClick(category)}
                          className="cursor-pointer"
                        >
                          <div className="flex items-start justify-between mb-3 pr-10">
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-lg text-gray-900 group-hover:text-blue-600 truncate">
                                {category.show_name}
                              </h3>
                              <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                                <span className="flex items-center">
                                  <Image className="w-4 h-4 mr-1" />
                                  {category.episode_count} 集
                                </span>
                                <span className="flex items-center">
                                  <Eye className="w-4 h-4 mr-1" />
                                  {category.total_images} 张图片
                                </span>
                              </div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 flex-shrink-0 ml-3" />
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">
                              最近浏览：{formatDateTime(category.last_viewed || (category.episodes.length > 0 ? category.episodes[0]?.created_at : ''))}
                            </span>
                            <span className="text-blue-600 font-medium text-sm bg-blue-50 px-2 py-1 rounded">
                              查看剧集
                            </span>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {/* 按集数显示模式 */}
              {displayMode === 'by_episode' && (
                <div className="space-y-3">
                  {getEpisodesList().map((episode) => {
                    const isFavorited = isEpisodeFavorited(episode.episode_name, episode.work_name)
                    return (
                      <div
                        key={`${episode.work_name}-${episode.episode_id}`}
                        className="group bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-lg transition-all relative"
                      >
                        {/* 收藏按钮 */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleEpisodeFavorite(episode, episode.work_name, isFavorited)
                          }}
                          className={`absolute top-3 right-3 z-10 p-1.5 rounded-full transition-all ${
                            isFavorited 
                              ? 'text-red-500 hover:text-red-600 bg-red-50 hover:bg-red-100' 
                              : 'text-gray-400 hover:text-red-500 bg-white hover:bg-red-50'
                          } shadow-sm border border-gray-200`}
                          title={isFavorited ? '取消收藏' : '收藏剧集'}
                        >
                          <Heart className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`} />
                        </button>

                        <div 
                          onClick={() => handleEpisodeClick(episode)}
                          className="cursor-pointer"
                        >
                          <div className="flex items-start justify-between mb-3 pr-10">
                            <div className="flex-1 min-w-0">
                              <h3 className="font-medium text-gray-900 group-hover:text-blue-600 truncate">
                                {episode.work_name} - {episode.episode_name}
                              </h3>
                              <p className="text-sm text-gray-500 mt-1">
                                来源搜索：{episode.search_term}
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                创建时间：{formatDateTime(episode.created_at)}
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4 text-sm text-gray-600">
                              <span className="flex items-center">
                                <Image className="w-4 h-4 mr-1" />
                                {episode.image_count} 张图片
                              </span>
                              <span className="inline-block px-2 py-1 text-xs font-medium text-purple-700 bg-purple-100 rounded">
                                {episode.source}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="text-blue-600 font-medium text-sm bg-blue-50 px-2 py-1 rounded">
                                查看图片
                              </span>
                              <button
                                onClick={(e) => handleDeleteClick(episode, episode.work_name, e)}
                                className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                title="删除历史记录"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8">
              <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">暂无搜索历史分类</h3>
              <p className="text-gray-500 mb-4">
                当您通过搜索功能查看作品集数时，系统会自动创建智能分类
              </p>
              <button
                onClick={() => navigate('/search')}
                className="btn btn-primary"
              >
                开始搜索
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default SearchBasedLibraryPage